import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type IconVariant = 'outline' | 'solid';
export type IconColor = 'primary' | 'secondary' | 'accent' | 'text' | 'muted' | 'success' | 'error' | 'warning' | 'info';

interface IconProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  size?: IconSize;
  color?: IconColor;
  variant?: IconVariant;
  className?: string;
  hover?: boolean;
  animate?: boolean;
  background?: boolean;
  backgroundGradient?: boolean;
}

const IconContainer = styled.span<{
  size: IconSize;
  color: string;
  hover: boolean;
  animate: boolean;
  background: boolean;
  backgroundGradient: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  transition: all 0.2s ease;

  ${props => {
    const sizes = {
      xs: '12px',
      sm: '16px',
      md: '20px',
      lg: '24px',
      xl: '32px',
      '2xl': '40px',
    };
    return `
      width: ${sizes[props.size]};
      height: ${sizes[props.size]};
    `;
  }}

  svg {
    width: 100%;
    height: 100%;
  }

  ${props => props.background && `
    padding: ${spacing[2]};
    border-radius: ${props.theme.borders?.radius?.md || '8px'};
    background: ${props.backgroundGradient 
      ? props.theme.colors.gradientPrimary 
      : props.theme.colors.surfaceHover};
  `}

  ${props => props.hover && `
    cursor: pointer;
    &:hover {
      transform: scale(1.1);
      color: ${props.theme.colors.primary};
    }
  `}

  ${props => props.animate && `
    animation: pulse 2s ease-in-out infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

export const Icon: React.FC<IconProps> = ({
  icon: IconComponent,
  size = 'md',
  color = 'text',
  variant = 'outline',
  className,
  hover = false,
  animate = false,
  background = false,
  backgroundGradient = false,
}) => {
  const theme = useTheme();
  
  const colorMap: Record<IconColor, string> = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    text: theme.colors.text,
    muted: theme.colors.textMuted,
    success: theme.colors.success,
    error: theme.colors.error,
    warning: theme.colors.warning,
    info: theme.colors.primary || theme.colors.accent,
  };

  return (
    <IconContainer
      size={size}
      color={colorMap[color]}
      hover={hover}
      animate={animate}
      background={background}
      backgroundGradient={backgroundGradient}
      className={className}
    >
      <IconComponent />
    </IconContainer>
  );
};

export default Icon;
